import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, RefreshCw, Download, FileText, AlertCircle, Users2, PoundSterling } from "lucide-react";
import { formatDate } from "@/lib/dates";

function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function lastDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}
function fmtMoney(v) {
  if (v === null || v === undefined) return "—";
  return "£" + Number(v).toFixed(2);
}

export default function PayrollRunPage() {
  const [from, setFrom] = useState(firstDayOfMonth());
  const [to, setTo] = useState(lastDayOfMonth());
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.get(`/payroll-run?from_date=${from}&to_date=${to}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load payroll run");
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (kind /* csv | pdf */) => {
    setBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/payroll-run/export.${kind}?from_date=${from}&to_date=${to}`;
      const token = localStorage.getItem("cc_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `payroll_run_${from}_${to}.${kind}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const nonPerm = useMemo(() => (data?.rows || []).filter((r) => r.bucket === "non_permanent"), [data]);
  const perm    = useMemo(() => (data?.rows || []).filter((r) => r.bucket === "permanent"), [data]);

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-2">Payroll</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3">
            <FileSignature className="w-8 h-8" /> Payroll Run
          </h1>
          <p className="text-[var(--cc-text-soft)] mt-1 text-sm max-w-2xl">
            Every payment due in the selected window, split by permanent / non-permanent staff.
            Salaried rows show the fixed period estimate; hourly rows show the agreed rate — the
            hours themselves are agreed with payroll outside the system. Leavers stop appearing
            after their last working day.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost inline-flex items-center gap-1.5" onClick={load} disabled={busy} data-testid="pr-refresh-button">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button className="btn-secondary inline-flex items-center gap-1.5" onClick={() => download("csv")} disabled={busy || !data} data-testid="pr-export-csv-button">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button className="btn-primary inline-flex items-center gap-1.5" onClick={() => download("pdf")} disabled={busy || !data} data-testid="pr-export-pdf-button">
            <FileText className="w-4 h-4" /> Export PDF
          </button>
        </div>
      </div>

      <div className="cc-card p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="pr-filters">
        <div>
          <label className="label-eyebrow block mb-1">Payment due from</label>
          <input type="date" className="cc-input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pr-from" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Payment due to</label>
          <input type="date" className="cc-input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="pr-to" />
        </div>
        <div className="md:col-span-2 flex items-end">
          <button className="btn-primary w-full md:w-auto" onClick={load} disabled={busy} data-testid="pr-apply-button">
            {busy ? "Loading…" : "Apply"}
          </button>
        </div>
      </div>

      {error && (
        <div className="cc-card p-3 mb-4 bg-red-50 border-l-4 border-red-500 text-xs flex items-start gap-2" data-testid="pr-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
          <div>{error}</div>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="pr-kpis">
            <Kpi title="Total payments" value={data.counts.total} icon={FileSignature} />
            <Kpi title="Permanent" value={data.counts.permanent} icon={Users2} />
            <Kpi title="Non-permanent" value={data.counts.non_permanent} icon={Users2} />
            <Kpi title="Total pay (salaried)" value={fmtMoney(data.totals.pay_amount)} icon={PoundSterling} accent />
          </div>

          <PayrollTable
            title="Non-permanent (bank / zero-hours / hourly)"
            rows={nonPerm}
            includeRate={true}
            emptyMsg="No non-permanent payments due in this window."
            testIdPrefix="pr-nonperm"
          />

          <PayrollTable
            title="Permanent / fixed-term"
            rows={perm}
            includeRate={false}
            emptyMsg="No salaried payments due in this window."
            testIdPrefix="pr-perm"
          />
        </>
      )}
    </div>
  );
}

function Kpi({ title, value, icon: Icon, accent }) {
  return (
    <div className="cc-card p-5" data-testid={`pr-kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className={`font-display text-3xl font-bold ${accent ? "text-[var(--cc-sage)]" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function PayrollTable({ title, rows, includeRate, emptyMsg, testIdPrefix }) {
  const subtotalPay   = rows.reduce((s, r) => s + (r.pay_amount || 0), 0);

  return (
    <div className="mb-8" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display text-xl font-bold">{title}</h2>
        <span className="text-xs text-[var(--cc-text-soft)]">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
      </div>
      <div className="cc-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-4 py-3 label-eyebrow">Employee</th>
              <th className="px-4 py-3 label-eyebrow">Contract</th>
              <th className="px-4 py-3 label-eyebrow">Due Date</th>
              <th className="px-4 py-3 label-eyebrow">Period</th>
              {includeRate && <th className="px-4 py-3 label-eyebrow text-right">Rate</th>}
              <th className="px-4 py-3 label-eyebrow text-right">Pay</th>
              <th className="px-4 py-3 label-eyebrow">Status</th>
              <th className="px-4 py-3 label-eyebrow">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={includeRate ? 8 : 7} className="text-center py-8 text-[var(--cc-text-soft)]" data-testid={`${testIdPrefix}-empty`}>
                  {emptyMsg}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={`${r.employee_id}-${r.period_id}`} className="border-t border-[var(--cc-border)]" data-testid={`${testIdPrefix}-row-${r.employee_id}-${r.period_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.employee_name || "—"}</div>
                  {r.work_email && <div className="text-[11px] text-[var(--cc-text-soft)]">{r.work_email}</div>}
                </td>
                <td className="px-4 py-3 text-xs">{r.contract_type || "—"}</td>
                <td className="px-4 py-3 text-xs font-mono">{formatDate(r.payment_due_date)}</td>
                <td className="px-4 py-3 text-xs">
                  {formatDate(r.work_start)} – {formatDate(r.work_end)}
                </td>
                {includeRate && (
                  <td className="px-4 py-3 text-right font-mono">
                    {r.hourly_rate != null ? `£${Number(r.hourly_rate).toFixed(2)}` : "—"}
                  </td>
                )}
                <td className="px-4 py-3 text-right font-mono font-semibold">
                  {fmtMoney(r.pay_amount)}
                </td>
                <td className="px-4 py-3">
                  <span className={`pill !text-[10px] ${r.paid_status === "paid" ? "pill-success" : "pill-warning"}`}>
                    {r.paid_status || "scheduled"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--cc-text-soft)] max-w-xs truncate" title={r.notes || ""}>
                  {r.notes || "—"}
                </td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="border-t-2 border-[var(--cc-border)] bg-[var(--cc-surface)] font-semibold" data-testid={`${testIdPrefix}-subtotal`}>
                <td colSpan={4} className="px-4 py-3 text-right">Subtotal</td>
                {includeRate && <td></td>}
                <td className="px-4 py-3 text-right font-mono">{fmtMoney(subtotalPay)}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
